// src/theme.ts
// -----------------------------------------------------------------------------
// SafetySound · Design System "Aurora Glass"
// -----------------------------------------------------------------------------
// Tema visual global. Aqui vive TODO el look & feel de la aplicacion:
// paleta suave (sin colores solidos fuertes), tipografia compacta, superficies
// de cristal (glassmorphism), bordes redondeados y sombras difusas.
//
// Cambiar un valor de `tokens` repinta la aplicacion completa.
// -----------------------------------------------------------------------------

import { createTheme, alpha } from '@mui/material/styles';
import type { Shadows } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';

/* ---------------------------------------------------------------- tokens -- */

export const tokens = {
  // Acento principal: aqua / teal
  brand: '#0E9F8F',
  brandLight: '#5FD6C6',
  brandDark: '#0A7A6E',
  brandSoft: 'rgba(14, 159, 143, 0.10)',

  // Acento secundario: violeta suave
  accent: '#7C6CF0',
  accentLight: '#B3A9F7',
  accentDark: '#5B4BD1',

  // Semanticos en tono pastel (nada de rojo/verde saturado)
  danger: '#E2708A',
  dangerDark: '#C24A66',
  warning: '#E9A23B',
  success: '#4FBF9F',
  info: '#5AA9E6',

  // Texto
  ink: '#12303A',
  inkSoft: '#5D7684',
  inkFaint: '#93A9B4',

  // Superficies de cristal
  glass: 'rgba(255, 255, 255, 0.68)',
  glassStrong: 'rgba(255, 255, 255, 0.86)',
  glassEdge: 'rgba(255, 255, 255, 0.70)',
  hairline: 'rgba(18, 48, 58, 0.09)',

  blur: 'blur(18px) saturate(170%)',
  blurSoft: 'blur(8px) saturate(150%)',

  radius: 14,
  radiusLg: 20,
  radiusPill: 999,
};

/** Fondo aurora usado en <body> y en la pantalla de login. */
export const auroraBackground = [
  'radial-gradient(1100px 620px at 6% -8%, rgba(95, 214, 198, 0.34), transparent 60%)',
  'radial-gradient(980px 640px at 96% 2%, rgba(148, 190, 255, 0.30), transparent 62%)',
  'radial-gradient(880px 700px at 46% 112%, rgba(179, 169, 247, 0.26), transparent 60%)',
  'linear-gradient(168deg, #F3FAFB 0%, #EEF4FB 46%, #F7F3FD 100%)',
].join(', ');

/* --------------------------------------------------------------- sombras -- */

const softShadow = (y: number, blur: number, a: number) => `0 ${y}px ${blur}px rgba(16, 58, 74, ${a})`;

const shadows = [
  'none',
  softShadow(2, 8, 0.05),
  softShadow(4, 14, 0.06),
  softShadow(6, 20, 0.07),
  softShadow(8, 26, 0.08),
  softShadow(10, 30, 0.08),
  softShadow(12, 34, 0.09),
  softShadow(14, 38, 0.09),
  softShadow(16, 42, 0.1),
  softShadow(18, 46, 0.1),
  softShadow(20, 50, 0.11),
  softShadow(22, 54, 0.11),
  softShadow(24, 58, 0.12),
  softShadow(26, 62, 0.12),
  softShadow(28, 66, 0.13),
  softShadow(30, 70, 0.13),
  softShadow(32, 74, 0.14),
  softShadow(34, 78, 0.14),
  softShadow(36, 82, 0.15),
  softShadow(38, 86, 0.15),
  softShadow(40, 90, 0.16),
  softShadow(42, 94, 0.16),
  softShadow(44, 98, 0.17),
  softShadow(46, 102, 0.17),
  softShadow(48, 106, 0.18),
] as unknown as Shadows;

/* ------------------------------------------------------------------ tema -- */

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: tokens.brand, light: tokens.brandLight, dark: tokens.brandDark, contrastText: '#FFFFFF' },
    secondary: { main: tokens.accent, light: tokens.accentLight, dark: tokens.accentDark, contrastText: '#FFFFFF' },
    error: { main: tokens.danger, light: '#F2A9B8', dark: tokens.dangerDark, contrastText: '#FFFFFF' },
    warning: { main: tokens.warning, light: '#F6CE92', dark: '#C77F1C', contrastText: '#3A2A08' },
    success: { main: tokens.success, light: '#9BE0CB', dark: '#2F9B7D', contrastText: '#FFFFFF' },
    info: { main: tokens.info, light: '#A8D2F2', dark: '#3B82BE', contrastText: '#FFFFFF' },
    text: { primary: tokens.ink, secondary: tokens.inkSoft, disabled: tokens.inkFaint },
    divider: tokens.hairline,
    background: { default: '#F1F6FA', paper: tokens.glass },
    action: {
      hover: 'rgba(14, 159, 143, 0.06)',
      selected: 'rgba(14, 159, 143, 0.12)',
      disabled: tokens.inkFaint,
      disabledBackground: 'rgba(18, 48, 58, 0.05)',
    },
  },

  shape: { borderRadius: tokens.radius },
  shadows,

  typography: {
    fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    h1: { fontWeight: 800, letterSpacing: '-0.03em' },
    h2: { fontWeight: 800, letterSpacing: '-0.03em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, fontSize: '1.2rem', letterSpacing: '-0.015em' },
    h6: { fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 600, fontSize: '0.9rem' },
    subtitle2: { fontWeight: 600, fontSize: '0.8rem' },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
    caption: { fontSize: '0.72rem', letterSpacing: '0.02em' },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
  },

  components: {
    /* ------------------------------------------------------------ global -- */
    MuiCssBaseline: {
      styleOverrides: {
        html: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
        body: {
          minHeight: '100vh',
          background: auroraBackground,
          backgroundAttachment: 'fixed',
          color: tokens.ink,
        },
        '#root': { minHeight: '100vh' },
        'h1, h2, h3, h4, h5, h6': {
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: tokens.ink,
        },
        hr: {
          border: 0,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${tokens.hairline}, transparent)`,
        },
        '::selection': { background: 'rgba(14, 159, 143, 0.18)' },
      },
    },

    /* --------------------------------------------------------- superficies */
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: tokens.glass,
          backdropFilter: tokens.blur,
          WebkitBackdropFilter: tokens.blur,
          border: `1px solid ${tokens.glassEdge}`,
          borderRadius: tokens.radiusLg,
          boxShadow: softShadow(10, 34, 0.07),
        },
        outlined: { border: `1px solid ${tokens.hairline}`, boxShadow: 'none' },
      },
    },

    MuiCard: { styleOverrides: { root: { overflow: 'hidden' } } },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.62)',
          backdropFilter: tokens.blur,
          WebkitBackdropFilter: tokens.blur,
          color: tokens.ink,
          border: 0,
          borderRadius: 0,
          borderBottom: `1px solid ${tokens.hairline}`,
          boxShadow: 'none',
        },
      },
    },

    MuiToolbar: { styleOverrides: { root: { minHeight: 64 } } },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(255, 255, 255, 0.58)',
          backdropFilter: tokens.blur,
          WebkitBackdropFilter: tokens.blur,
          border: 0,
          borderRight: `1px solid ${tokens.hairline}`,
          borderRadius: 0,
          boxShadow: 'none',
        },
      },
    },

    /* ----------------------------------------------------------- botones -- */
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: tokens.radiusPill,
          paddingLeft: 18,
          paddingRight: 18,
          boxShadow: 'none',
          transition: 'transform .18s ease, box-shadow .18s ease, background-color .18s ease, border-color .18s ease',
          '&:active': { transform: 'translateY(1px)' },
          '&.Mui-disabled': { opacity: 0.55 },
        },
        sizeSmall: { paddingLeft: 14, paddingRight: 14, paddingTop: 5, paddingBottom: 5, fontSize: '0.8125rem' },
        sizeLarge: { paddingLeft: 24, paddingRight: 24, paddingTop: 10, paddingBottom: 10, fontSize: '0.9375rem' },

        outlined: {
          backgroundColor: 'rgba(255, 255, 255, 0.55)',
          backdropFilter: tokens.blurSoft,
          WebkitBackdropFilter: tokens.blurSoft,
          borderColor: alpha(tokens.brand, 0.32),
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderColor: alpha(tokens.brand, 0.55),
            boxShadow: `0 8px 20px ${alpha(tokens.brand, 0.16)}`,
            transform: 'translateY(-1px)',
          },
        },
        outlinedError: {
          borderColor: alpha(tokens.danger, 0.34),
          '&:hover': {
            borderColor: alpha(tokens.danger, 0.6),
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            boxShadow: `0 8px 20px ${alpha(tokens.danger, 0.16)}`,
          },
        },
        outlinedSecondary: {
          borderColor: alpha(tokens.accent, 0.34),
          '&:hover': { borderColor: alpha(tokens.accent, 0.6), boxShadow: `0 8px 20px ${alpha(tokens.accent, 0.16)}` },
        },

        contained: {
          background: `linear-gradient(135deg, ${tokens.brandLight} -20%, ${tokens.brand} 55%, ${tokens.brandDark} 130%)`,
          boxShadow: `0 8px 22px ${alpha(tokens.brand, 0.26)}`,
          '&:hover': { boxShadow: `0 12px 28px ${alpha(tokens.brand, 0.34)}`, transform: 'translateY(-1px)' },
        },
        containedError: {
          background: `linear-gradient(135deg, #F2A9B8 -20%, ${tokens.danger} 55%, ${tokens.dangerDark} 130%)`,
          boxShadow: `0 8px 22px ${alpha(tokens.danger, 0.26)}`,
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${tokens.accentLight} -20%, ${tokens.accent} 55%, ${tokens.accentDark} 130%)`,
          boxShadow: `0 8px 22px ${alpha(tokens.accent, 0.26)}`,
        },

        text: { '&:hover': { backgroundColor: tokens.brandSoft } },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          color: tokens.inkSoft,
          transition: 'background-color .18s ease, color .18s ease, transform .18s ease',
          '&:hover': { backgroundColor: tokens.brandSoft, color: tokens.brandDark },
        },
        colorInherit: { color: 'inherit' },
        colorError: {
          color: tokens.danger,
          '&:hover': { backgroundColor: alpha(tokens.danger, 0.1), color: tokens.dangerDark },
        },
      },
    },

    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: `0 10px 26px ${alpha(tokens.brand, 0.3)}`,
          background: `linear-gradient(135deg, ${tokens.brandLight}, ${tokens.brand})`,
        },
      },
    },

    /* -------------------------------------------------------- formularios -- */
    MuiTextField: { defaultProps: { size: 'small', variant: 'outlined' } },
    MuiFormControl: { defaultProps: { size: 'small' } },
    MuiSelect: { defaultProps: { size: 'small' } },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: 'rgba(255, 255, 255, 0.58)',
          backdropFilter: tokens.blurSoft,
          WebkitBackdropFilter: tokens.blurSoft,
          transition: 'background-color .18s ease, box-shadow .18s ease',
          '& fieldset': { borderColor: tokens.hairline, transition: 'border-color .18s ease' },
          '& legend': { fontSize: '0.66em' },
          '&:hover fieldset': { borderColor: alpha(tokens.brand, 0.42) },
          '&.Mui-focused': {
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
            boxShadow: `0 0 0 4px ${alpha(tokens.brand, 0.12)}`,
          },
          '&.Mui-focused fieldset': { borderWidth: 1.5, borderColor: tokens.brand },
          '&.Mui-disabled': { backgroundColor: 'rgba(18, 48, 58, 0.035)' },
          // Los diálogos de consulta usan campos deshabilitados: el valor debe
          // seguir siendo legible, no gris claro.
          '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: tokens.inkSoft, opacity: 1 },
          '&.Mui-disabled fieldset': { borderColor: 'rgba(18, 48, 58, 0.07)' },
          '&.Mui-error.Mui-focused': { boxShadow: `0 0 0 4px ${alpha(tokens.danger, 0.13)}` },
        },
        input: { fontSize: '0.875rem' },
        inputSizeSmall: { padding: '8px 12px', fontSize: '0.8125rem' },
        notchedOutline: { borderColor: tokens.hairline },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          color: tokens.inkSoft,
          '&.Mui-focused': { color: tokens.brandDark },
          '&.Mui-disabled': { color: tokens.inkFaint },
        },
        sizeSmall: { fontSize: '0.8125rem' },
      },
    },

    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: '0.7rem', marginLeft: 4, marginTop: 3, letterSpacing: '0.01em' } },
    },

    MuiFormLabel: { styleOverrides: { root: { fontSize: '0.8125rem' } } },

    MuiCheckbox: { defaultProps: { size: 'small' }, styleOverrides: { root: { borderRadius: 8 } } },
    MuiRadio: { defaultProps: { size: 'small' } },
    MuiSwitch: { defaultProps: { size: 'small' } },

    MuiAutocomplete: {
      styleOverrides: {
        paper: { backgroundColor: tokens.glassStrong, borderRadius: tokens.radius },
        option: { fontSize: '0.8125rem', borderRadius: 10, margin: '2px 6px' },
      },
    },

    /* ------------------------------------------------------------ tablas -- */
    MuiTableContainer: {
      styleOverrides: { root: { borderRadius: tokens.radiusLg, overflowX: 'auto' } },
    },

    MuiTable: { defaultProps: { size: 'small' } },

    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: 'rgba(18, 48, 58, 0.06)', fontSize: '0.8125rem', color: tokens.ink },
        head: {
          fontWeight: 700,
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: tokens.inkSoft,
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          borderBottom: `1px solid ${tokens.hairline}`,
          lineHeight: 1.35,
        },
        sizeSmall: { padding: '9px 12px' },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color .16s ease',
          '&:not(.MuiTableRow-head):hover': { backgroundColor: 'rgba(14, 159, 143, 0.045)' },
          '&:last-of-type td': { borderBottom: 0 },
        },
      },
    },

    MuiTablePagination: {
      styleOverrides: {
        root: { border: 0, color: tokens.inkSoft },
        toolbar: { minHeight: 48, paddingLeft: 8, paddingRight: 8 },
        selectLabel: { fontSize: '0.75rem', marginBottom: 0 },
        displayedRows: { fontSize: '0.75rem', marginBottom: 0 },
        select: { fontSize: '0.75rem' },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${tokens.glassEdge}`,
          borderRadius: tokens.radiusLg,
          backgroundColor: tokens.glass,
          backdropFilter: tokens.blur,
          fontSize: '0.8125rem',
        },
        columnHeaders: { borderBottom: `1px solid ${tokens.hairline}` },
        columnHeader: {
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: tokens.inkSoft,
          },
        },
        cell: {
          borderColor: 'rgba(18, 48, 58, 0.06)',
          '&:focus, &:focus-within': { outline: `2px solid ${alpha(tokens.brand, 0.35)}`, outlineOffset: -2 },
        },
        row: {
          '&:hover': { backgroundColor: 'rgba(14, 159, 143, 0.05)' },
          '&.Mui-selected': { backgroundColor: 'rgba(14, 159, 143, 0.1)' },
        },
        footerContainer: { borderTop: `1px solid ${tokens.hairline}`, minHeight: 46 },
        virtualScroller: { backgroundColor: 'transparent' },
      },
    },

    /* ---------------------------------------------------------- dialogos -- */
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 22,
          backgroundColor: tokens.glassStrong,
          backdropFilter: 'blur(28px) saturate(180%)',
          WebkitBackdropFilter: 'blur(28px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.75)',
          boxShadow: '0 28px 80px rgba(16, 58, 74, 0.18)',
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0))',
        },
        // Los diálogos a pantalla completa no llevan borde ni esquinas redondeadas.
        paperFullScreen: {
          borderRadius: 0,
          border: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
        },
      },
    },

    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(14, 42, 54, 0.26)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          '&.MuiBackdrop-invisible': {
            backgroundColor: 'transparent',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.02rem',
          fontWeight: 700,
          letterSpacing: '-0.01em',
          padding: '18px 24px 12px',
          color: tokens.ink,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px 20px',
          // MUI pone paddingTop 0 cuando el contenido sigue a un DialogTitle.
          // Se recupera algo de aire para que la etiqueta flotante del primer
          // campo no quede recortada por el overflow del contenedor.
          '.MuiDialogTitle-root + &': { paddingTop: 8 },
        },
      },
    },
    MuiDialogActions: { styleOverrides: { root: { padding: '8px 20px 18px', gap: 8 } } },
    MuiDialogContentText: { styleOverrides: { root: { fontSize: '0.875rem', color: tokens.inkSoft } } },

    /* -------------------------------------------------------------- tabs -- */
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 42 },
        flexContainer: { gap: 4 },
        indicator: {
          height: '100%',
          borderRadius: tokens.radiusPill,
          backgroundColor: 'rgba(14, 159, 143, 0.12)',
          border: `1px solid ${alpha(tokens.brand, 0.18)}`,
          zIndex: 0,
        },
        scrollButtons: { color: tokens.inkSoft },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 38,
          borderRadius: tokens.radiusPill,
          fontWeight: 600,
          fontSize: '0.8125rem',
          color: tokens.inkSoft,
          paddingLeft: 16,
          paddingRight: 16,
          zIndex: 1,
          transition: 'color .18s ease',
          '&:hover': { color: tokens.brandDark },
          '&.Mui-selected': { color: tokens.brandDark },
        },
      },
    },

    /* ------------------------------------------------------------ listas -- */
    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: 12, transition: 'background-color .18s ease, color .18s ease' } },
    },
    MuiListItemIcon: { styleOverrides: { root: { minWidth: 38, color: 'inherit' } } },
    MuiListItemText: { styleOverrides: { primary: { fontSize: '0.85rem', fontWeight: 600 } } },

    MuiMenu: {
      styleOverrides: {
        paper: { backgroundColor: tokens.glassStrong, borderRadius: tokens.radius, padding: 4 },
        list: { padding: 0 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          borderRadius: 10,
          margin: '2px 0',
          minHeight: 34,
          '&:hover': { backgroundColor: tokens.brandSoft },
          '&.Mui-selected': { backgroundColor: 'rgba(14, 159, 143, 0.14)' },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(14, 159, 143, 0.2)' },
        },
      },
    },
    MuiPopover: { styleOverrides: { paper: { backgroundColor: tokens.glassStrong } } },

    /* ------------------------------------------------------------ varios -- */
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: tokens.radiusPill, fontWeight: 600, fontSize: '0.72rem', height: 24 },
        outlined: { backgroundColor: 'rgba(255,255,255,0.6)' },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: '0.85rem',
          fontWeight: 700,
          width: 34,
          height: 34,
          background: `linear-gradient(135deg, ${tokens.brandLight}, ${tokens.brand})`,
          color: '#FFFFFF',
          border: '2px solid rgba(255,255,255,0.75)',
          boxShadow: `0 4px 12px ${alpha(tokens.brand, 0.28)}`,
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(18, 48, 58, 0.92)',
          backdropFilter: 'blur(6px)',
          borderRadius: 10,
          fontSize: '0.72rem',
          fontWeight: 500,
          padding: '6px 10px',
        },
        arrow: { color: 'rgba(18, 48, 58, 0.92)' },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: tokens.hairline } } },

    MuiCircularProgress: { styleOverrides: { root: { color: tokens.brand } } },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: tokens.radiusPill, height: 6, backgroundColor: tokens.brandSoft },
        bar: { borderRadius: tokens.radiusPill, background: `linear-gradient(90deg, ${tokens.brandLight}, ${tokens.brand})` },
      },
    },

    MuiAlert: {
      styleOverrides: { root: { borderRadius: tokens.radius, fontSize: '0.8125rem', border: `1px solid ${tokens.hairline}` } },
    },
  },
});

export default theme;
